import { supabase } from './supabase';

// First-party analytics → Supabase `events` table (see supabase/analytics.sql).
// Everything here is fire-and-forget and wrapped in try/catch, so tracking can
// NEVER break the app — if the table doesn't exist yet it just does nothing.
// No message text, names or phone numbers are ever sent.

const VID_KEY = 'yhuu_vid';
export const NO_TRACK_KEY = 'yhuu_no_track'; // set on the operator's own device

const CHOICE_KEY = 'yhuu_track_choice'; // set once the operator picks on/off themselves

export function isTrackingOff(): boolean {
  try { return !!localStorage.getItem(NO_TRACK_KEY); } catch { return false; }
}
export function setTrackingOff(off: boolean) {
  try {
    if (off) localStorage.setItem(NO_TRACK_KEY, '1'); else localStorage.removeItem(NO_TRACK_KEY);
    localStorage.setItem(CHOICE_KEY, '1');
  } catch { /* ignore */ }
}
// Default for the operator's device: don't count it — unless they already chose.
export function excludeOperatorByDefault() {
  try { if (!localStorage.getItem(CHOICE_KEY)) localStorage.setItem(NO_TRACK_KEY, '1'); } catch { /* ignore */ }
}

let currentUserId: string | null = null;
export const setAnalyticsUser = (id: string | null) => { currentUserId = id; };

function visitorId(): string {
  try {
    let id = localStorage.getItem(VID_KEY);
    if (!id) {
      id = (crypto as any)?.randomUUID?.() ?? `v_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem(VID_KEY, id!);
    }
    return id!;
  } catch {
    return 'unknown';
  }
}

function deviceType(): string {
  const ua = navigator.userAgent || '';
  if (/iPad|Tablet/i.test(ua)) return 'tablet';
  if (/Mobi|Android|iPhone/i.test(ua)) return 'mobile';
  return 'desktop';
}

// /test/3f2a… → /test/:id so every session groups under one path.
function cleanPath(p: string): string {
  return p.replace(/\/[0-9a-f]{8}-[0-9a-f-]{20,}/gi, '/:id').replace(/\/[0-9a-z_-]{20,}/gi, '/:id');
}

// Where did they come from? ?utm_source= / ?ref= wins, else the referring site.
function referrerSource(): string | null {
  try {
    const q = new URLSearchParams(window.location.search);
    const tagged = q.get('utm_source') || q.get('ref');
    if (tagged) return tagged.slice(0, 60);
    if (document.referrer) {
      const host = new URL(document.referrer).hostname.replace(/^www\./, '');
      if (host && host !== window.location.hostname) return host.slice(0, 60);
    }
  } catch { /* ignore */ }
  return null;
}

const ERR_KEY = 'yhuu_track_err';
function rememberError(e: any) {
  try {
    const msg = `${e?.message || 'request failed'}${e?.code ? ` (${e.code})` : ''}`;
    localStorage.setItem(ERR_KEY, msg.slice(0, 200));
  } catch { /* ignore */ }
}
export function lastTrackingError(): string {
  try { return localStorage.getItem(ERR_KEY) || ''; } catch { return ''; }
}
export function clearTrackingError() { try { localStorage.removeItem(ERR_KEY); } catch { /* ignore */ } }

// Operator-only: send one event right now and report exactly what happened.
export async function sendTestEvent(): Promise<string> {
  try {
    const { error } = await supabase.from('events').insert({
      event: 'test_event', visitor_id: visitorId(), user_id: currentUserId,
      path: cleanPath(window.location.pathname), device: deviceType(), meta: {},
    });
    if (error) {
      const e: any = error;
      return `❌ ${e.message || 'failed'}${e.code ? ` (${e.code})` : ''}${e.hint ? ` — ${e.hint}` : ''}`;
    }
    clearTrackingError();
    return '✅ Saved! Tap “Refresh numbers” to see it counted.';
  } catch (e: any) {
    return `❌ ${e?.message || 'Request failed'}`;
  }
}

export function track(event: string, meta: Record<string, unknown> = {}, testId?: string | null) {
  try {
    if (localStorage.getItem(NO_TRACK_KEY)) return;
    const row = {
      event,
      visitor_id: visitorId(),
      user_id: currentUserId,
      test_id: testId ?? null,
      path: cleanPath(window.location.pathname),
      referrer: event === 'page_view' ? referrerSource() : null,
      device: deviceType(),
      meta,
    };
    (supabase.from('events').insert(row) as any).then(
      (res: any) => { if (res?.error) rememberError(res.error); },
      (err: any) => rememberError(err),
    );
  } catch { /* never break the app */ }
}

// Any element with data-track="name" logs a `click:name` event when tapped.
let clickBound = false;
export function initClickTracking() {
  if (clickBound || typeof document === 'undefined') return;
  clickBound = true;
  document.addEventListener('click', (e) => {
    const el = (e.target as HTMLElement | null)?.closest?.('[data-track]') as HTMLElement | null;
    const name = el?.getAttribute('data-track');
    if (name) track(`click:${name}`);
  }, true);
}
